import {
  queryGroupByForView,
  queryPlanForView,
  type DatabaseView,
} from '@couplespace/core/databases';

import { queryDatabase } from './api';
import { buildDatabaseQueryInput } from './build-query';
import type { DatabaseQueryInput, DatabaseQueryResult } from './types';

const MAX_PAGES = 50;

type QueryPage = (
  databaseId: string,
  body: DatabaseQueryInput,
) => Promise<DatabaseQueryResult>;

/** Follow position cursors until the view's matching rows are all loaded. */
export async function queryAllDatabaseRows(
  databaseId: string,
  input: DatabaseQueryInput,
  fetchPage: QueryPage = queryDatabase,
): Promise<DatabaseQueryResult> {
  const rows: DatabaseQueryResult['rows'] = [];
  const propertiesByRowId: NonNullable<DatabaseQueryResult['propertiesByRowId']> = {};
  const seen = new Set<string>();
  let cursor: string | undefined;
  let footers: DatabaseQueryResult['footers'];

  for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
    const page = await fetchPage(databaseId, { ...input, cursor });
    let added = 0;
    for (const row of page.rows) {
      if (seen.has(row.id)) {
        continue;
      }
      seen.add(row.id);
      rows.push(row);
      const props = page.propertiesByRowId?.[row.id];
      if (props) {
        propertiesByRowId[row.id] = props;
      }
      added += 1;
    }
    if (page.footers) {
      footers = page.footers;
    }
    const next = page.pageInfo.nextCursor;
    if (!page.pageInfo.hasMore || !next || next === cursor || added === 0) {
      break;
    }
    cursor = next;
  }

  return {
    rows,
    propertiesByRowId,
    footers,
    pageInfo: { hasMore: false, nextCursor: null },
  };
}

export async function queryDatabaseForView(
  databaseId: string,
  view: DatabaseView,
  search: string,
  options?: { columnAggregates?: DatabaseView['columnAggregates'] },
): Promise<DatabaseQueryResult> {
  const plan = queryPlanForView(view);
  const input = buildDatabaseQueryInput({
    search,
    filters: view.filters,
    sorts: plan.sorts,
    groupBy: queryGroupByForView(view),
    columnAggregates: options?.columnAggregates,
  });
  if (plan.allRows) {
    return queryAllDatabaseRows(databaseId, input);
  }
  return queryDatabase(databaseId, input);
}
